import React, { useState, useEffect } from 'react';
import AddressDrawer from './AddressDrawer';
import PaymentPage from './PaymentSection';
import { useCart, CartItem } from '../Header/CartContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaExclamationTriangle, FaTruck } from 'react-icons/fa'; 

interface Address {
  id: string;
  name: string;          
  mobileNumber: string;
  pincode: string;       
  locality: string;      
  addressLine1: string;
  city: string;
  state: string;
  addressType: string;   
}

const CheckoutPage = () => {
  const [step, setStep] = useState<'review' | 'payment'>('review');
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [showAddressDrawer, setShowAddressDrawer] = useState(false);

  // ✅ New States for Serviceability Check
  const [isServiceable, setIsServiceable] = useState<boolean>(true); // Default true rakha hai
  const [checkingServiceability, setCheckingServiceability] = useState<boolean>(false);
  const [deliveryMessage, setDeliveryMessage] = useState<string>('');

  const { cart } = useCart();
  const navigate = useNavigate();
  const [checkoutItems, setCheckoutItems] = useState<CartItem[]>([]);

  // 1. Fetch Addresses
  const fetchAddresses = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (!user.id) return;
      
      const res = await axios.get<Address[]>(`https://ayushvatika.com/api/addresses/user/${user.id}`);
      setAddresses(res.data);

      const lastSelected = localStorage.getItem("selectedAddress");
      if (lastSelected && res.data.some(addr => addr.id === lastSelected)) {
        setSelectedAddress(lastSelected);
      } else if (res.data.length > 0) {
        setSelectedAddress(res.data[0].id);
      }
    } catch (err) {
      console.error("Failed to fetch addresses", err);
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  // 2. Logic to Check Serviceability whenever Address Changes
  useEffect(() => {
    const checkDelivery = async () => {
        if (!selectedAddress) return;
        
        const currentAddr = addresses.find(a => a.id === selectedAddress);
        if (!currentAddr) return;

        setCheckingServiceability(true);
        setDeliveryMessage('');
        setIsServiceable(true); // Reset pehle

        try {
            // ✅ Backend API Call to check Pincode
            const res = await axios.post('https://ayushvatika.com/api/delivery/serviceability', {
                deliveryPin: currentAddr.pincode,
                pickupPin: "201301" // Aapka Warehouse Pincode
            });

            if (res.data.serviceable) {
                setIsServiceable(true);
                const days = res.data.estimatedDays || 5;
                // Date calculate karna
                const date = new Date();
                date.setDate(date.getDate() + days);
                setDeliveryMessage(`Delivery available! Expected by ${date.toDateString()}`);
            } else {
                setIsServiceable(false);
                setDeliveryMessage("Sorry, delivery is NOT available at this location.");
            }
        } catch (error) {
            console.error("Serviceability Check Failed", error);
            // Agar API fail ho jaye, to hum assume kar sakte hain ki service nahi hai ya error dikha dein
            setIsServiceable(true); 
        } finally {
            setCheckingServiceability(false);
        }
    };

    if (selectedAddress && addresses.length > 0) {
        localStorage.setItem("selectedAddress", selectedAddress);
        checkDelivery();
    }
  }, [selectedAddress, addresses]);


  // Cart & Buy Now Logic
  useEffect(() => {
    const checkoutMode = localStorage.getItem('checkoutMode');
    const buyNowProductString = localStorage.getItem('buyNowProduct');

    if (checkoutMode === 'buyNow' && buyNowProductString) {
      try {
        const buyNowProduct: CartItem = JSON.parse(buyNowProductString);
        setCheckoutItems([buyNowProduct]);
      } catch (e) {
        setCheckoutItems(cart);
      }
    } else {
      setCheckoutItems(cart);
    }
  }, [cart]);

  const totalPrice = checkoutItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
  const discount = 10;
  const grandTotal = totalPrice > discount ? totalPrice - discount : 0;

  // Address Save Handler
  const handleAddressSave = async (newAddressData: any) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      if (!user.id) {
        toast.error("User not found.");
        return;
      }
      const res = await axios.post(`https://ayushvatika.com/api/addresses/user/${user.id}`, newAddressData);
      toast.success("Address saved!");
      setShowAddressDrawer(false);
      const updatedRes = await axios.get<Address[]>(`https://ayushvatika.com/api/addresses/user/${user.id}`);
      setAddresses(updatedRes.data);
      if (res.data && res.data.id) setSelectedAddress(res.data.id);
    } catch (err: any) {
      toast.error("Failed to save address.");
    }
  };

  const handlePaymentSuccess = () => {
    setStep('review');
    setCheckoutItems([]);
    localStorage.removeItem('buyNowProduct');
    localStorage.removeItem('checkoutMode');
  };

  const currentAddress = addresses.find(addr => addr.id === selectedAddress);

  return (
    <div className="container mx-auto p-4 md:p-8 min-h-screen bg-gray-100 font-inter">
      <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">Checkout</h1>

      {/* Stepper */}
      <div className="flex justify-center mb-8">
        <div className={`px-6 py-2 rounded-l-lg transition-colors duration-300 ${step === 'review' ? 'bg-purple-600 text-white font-semibold' : 'bg-gray-300 text-gray-700'}`}>
          1. Review Order
        </div>
        <div className={`px-6 py-2 rounded-r-lg transition-colors duration-300 ${step === 'payment' ? 'bg-purple-600 text-white font-semibold' : 'bg-gray-300 text-gray-700'}`}>
          2. Payment
        </div>
      </div>

      {step === 'review' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            
            {/* Delivery Address Section */}
            <div className={`bg-white p-6 rounded-lg shadow-md border-l-4 ${isServiceable ? 'border-purple-600' : 'border-red-500'}`}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-bold text-xl text-gray-800 flex items-center">
                   📍 Delivery Address
                </h2>
                <button
                  onClick={() => setShowAddressDrawer(true)}
                  className="text-purple-600 hover:text-purple-800 text-sm font-bold uppercase tracking-wide border border-purple-200 px-3 py-1 rounded hover:bg-purple-50 transition"
                >
                  {addresses.length > 0 ? "Change" : "+ Add Address"}
                </button>
              </div>

              {!currentAddress ? (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <p className="text-gray-500 mb-2">No delivery address selected.</p>
                  <button onClick={() => setShowAddressDrawer(true)} className="text-purple-600 font-semibold hover:underline">
                    + Add New Address
                  </button>
                </div>
              ) : (
                <div className="text-gray-700 animate-fade-in">
                  <div className="flex items-center gap-2 mb-1">
                      <p className="font-bold text-lg text-gray-900">{currentAddress.name}</p>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded font-medium">{currentAddress.addressType}</span>
                  </div>
                  <p className="font-medium mb-1">{currentAddress.addressLine1}, {currentAddress.locality}</p>
                  <p className="mb-1">{currentAddress.city}, {currentAddress.state} - <span className="font-semibold">{currentAddress.pincode}</span></p>
                  <p className="text-sm font-semibold text-gray-600">Mobile: {currentAddress.mobileNumber}</p>

                  {/* ✅ Serviceability Message Box */}
                  <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 text-sm font-bold ${
                      checkingServiceability ? 'bg-gray-100 text-gray-600' :
                      isServiceable ? 'bg-green-50 text-green-700 border border-green-200' :
                      'bg-red-50 text-red-600 border border-red-200'
                  }`}>
                      {checkingServiceability ? (
                          <><span>🔄</span> Checking delivery availability...</>
                      ) : isServiceable ? (
                          <><FaTruck size={16}/> {deliveryMessage}</>
                      ) : (
                          <><FaExclamationTriangle size={16}/> {deliveryMessage}</>
                      )}
                  </div>
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="font-bold text-xl text-gray-800 mb-4">Order Items ({checkoutItems.length})</h2>
              {checkoutItems.length === 0 ? (
                 <div className="text-center text-gray-600 py-8">
                  <p className="mb-2">Your checkout bag is empty!</p>
                  <button onClick={() => navigate('/')} className="text-purple-600 hover:underline">Continue Shopping</button>
                </div>
              ) : (
                checkoutItems.map((item) => (
                  <div key={item.id} className="flex gap-4 border-b last:border-0 py-4 items-center group">
                    <img 
                      src={item.imageUrl || '/Logo/default-product.png'} 
                      alt={item.productName} 
                      onClick={() => navigate(`/product/${item.productId}`)}
                      className="w-20 h-20 object-cover rounded-md border border-gray-200 cursor-pointer group-hover:scale-105 transition-transform" 
                    />
                    <div className="flex-1">
                      <h3 
                        onClick={() => navigate(`/product/${item.productId}`)}
                        className="font-semibold text-gray-800 cursor-pointer hover:text-purple-600 transition-colors"
                      >
                        {item.productName}
                      </h3>
                      <p className="font-bold text-gray-900 mt-1">₹{item.price}</p>
                    </div>
                    <div className="px-4 py-1 bg-gray-100 rounded text-gray-700 font-medium">Qty: {item.quantity}</div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Price Summary & Payment Button */}
          <div className="lg:col-span-1">
             <div className="bg-white p-6 rounded-lg shadow-md h-fit sticky top-24">
                <h2 className="font-bold text-xl text-gray-800 mb-4 border-b pb-2">Price Details</h2>
                <div className="space-y-3 text-gray-700 mb-6">
                    <div className="flex justify-between"><span>Total MRP</span><span>₹{totalPrice}</span></div>
                    <div className="flex justify-between text-green-600"><span>Discount</span><span>-₹{discount}</span></div>
                    <div className="border-t pt-3 flex justify-between font-bold text-lg text-gray-900"><span>Total Amount</span><span>₹{grandTotal}</span></div>
                </div>
                
                {/* ✅ Button Logic Updated: Disable if NOT Serviceable */}
                <button
                onClick={() => setStep('payment')}
                disabled={!selectedAddress || checkoutItems.length === 0 || !isServiceable || checkingServiceability}
                className={`w-full py-3 rounded-lg font-bold text-lg shadow-lg transition-all transform hover:-translate-y-1 
                    ${(!selectedAddress || checkoutItems.length === 0 || !isServiceable || checkingServiceability)
                        ? 'bg-gray-400 cursor-not-allowed opacity-70' 
                        : 'bg-orange-500 text-white hover:bg-orange-600'
                    }`}
                >
                {checkingServiceability ? "Checking Pincode..." :
                 !isServiceable ? "🚫 No Delivery Here" :
                 checkoutItems.length === 0 ? "Cart is Empty" : 
                 !selectedAddress ? "Select Address" : 
                 "Proceed to Payment"}
                </button>

                {!isServiceable && !checkingServiceability && (
                    <p className="text-xs text-red-500 mt-3 text-center font-semibold">
                        Please change delivery address to proceed.
                    </p>
                )}
             </div>
          </div>
        </div>
      )}

      {step === 'payment' && (
        <PaymentPage total={grandTotal} onPaymentSuccess={handlePaymentSuccess} />
      )}

      <AddressDrawer
        open={showAddressDrawer}
        onClose={() => setShowAddressDrawer(false)}
        onSave={handleAddressSave}
        addresses={addresses} 
        selectedAddressId={selectedAddress} 
        onSelectAddress={(id) => {
          setSelectedAddress(id);
          setShowAddressDrawer(false); 
        }}
      />
    </div>
  );
};

export default CheckoutPage;